'use client';

import { useState, useEffect, useRef } from 'react';
import { TextInput, Text, Loading } from '@instamenu/ui-system-next';
import { captureClientException } from '@instamenu/posthog/client';
import { optionStyle, optionsContainer } from './places.css';
import { useApiIsLoaded, useMapsLibrary } from '@vis.gl/react-google-maps';
import { Address } from '@instamenu/context-user';
import { placeToAddress } from './place-to-address';

export type AddressAutocompleteProps = {
  onChange: (address: Address | undefined) => void;
  initialFormattedAddress?: string;
  isLocationInputDisabled?: boolean;
};

export const AddressAutocomplete = ({
  initialFormattedAddress = '',
  onChange,
  isLocationInputDisabled,
}: AddressAutocompleteProps) => {
  const places = useMapsLibrary('places');
  const apiIsLoaded = useApiIsLoaded();

  const [inputValue, setInputValue] = useState(initialFormattedAddress);
  const [suggestions, setSuggestions] = useState<google.maps.places.AutocompleteSuggestion[]>([]);
  const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken | null>(null);
  const [showSuggestions, setShowSuggestions] = useState(false);

  useEffect(() => {
    if (!places || !inputValue) {
      setSuggestions([]);
      return;
    }

    if (!sessionTokenRef.current) {
      sessionTokenRef.current = new places.AutocompleteSessionToken();
    }

    const request: google.maps.places.AutocompleteRequest = {
      input: inputValue,
      sessionToken: sessionTokenRef.current,
      includedRegionCodes: ['us'],
    };

    places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request)
      .then((response) => {
        setSuggestions(response.suggestions);
      })
      .catch((error) => {
        captureClientException(error, {
          source: 'ui-storefront.places.fetch-autocomplete-suggestions',
          properties: {
            inputValueLength: inputValue.length,
          },
        });
        setSuggestions([]);
      });
  }, [places, inputValue]);

  const onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setInputValue(value);

    if (!value) {
      setSuggestions([]);
      setShowSuggestions(false);
      onChange(undefined);
      sessionTokenRef.current = null;
      return;
    }
    setShowSuggestions(true);
  };

  const handleSuggestionClick = (suggestion: google.maps.places.AutocompleteSuggestion) => {
    if (!places || !suggestion.placePrediction) {
      return;
    }

    const placePrediction = suggestion.placePrediction;
    const place = placePrediction.toPlace();

    const fields = ['addressComponents', 'location'];

    place
      .fetchFields({ fields })
      .then((fetchedPlace) => {
        onChange(placeToAddress(fetchedPlace.place));
        setInputValue(placeToAddress(fetchedPlace.place)?.formattedAddress ?? '');
        sessionTokenRef.current = null;
        setSuggestions([]);
        setShowSuggestions(false);
      })
      .catch((error) => {
        captureClientException(error, {
          source: 'ui-storefront.places.fetch-place-details',
          properties: {
            placeId: suggestion.placePrediction?.placeId,
          },
        });
      });
  };

  if (!apiIsLoaded) return <Loading />;
  return (
    <div style={{ position: 'relative' }}>
      <TextInput
        value={inputValue}
        onChange={onInputChange}
        disabled={!places || isLocationInputDisabled}
        placeholder="123 Main St, Austin, TX 12345"
        autoComplete="off"
      />
      {showSuggestions && (
        <div className={optionsContainer}>
          {suggestions.length > 0 &&
            suggestions.map((suggestion) => {
              const prediction = suggestion.placePrediction;
              return (
                <div
                  className={optionStyle}
                  key={prediction?.placeId}
                  onClick={() => handleSuggestionClick(suggestion)}
                  data-id="places-suggestion"
                >
                  <Text variant="caption2">{prediction?.text.text}</Text>
                </div>
              );
            })}
        </div>
      )}
    </div>
  );
};
